import React, { useState } from 'react';
import TextInput from 'design-system/TextInput';
import SanitizedMarkdown from 'design-system/SanitizedMarkdown';
import { Label, Button } from 'design-system/Form';
import { IconButton } from 'react-scale/components/design-system';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import apiFetch, { defaultApiFetchErrorHandler } from '@scale/frontend-lib/api-fetch';
import { DeliveryTaskProblems } from '@scale/shared/corp/dynamicCorpTools/flamingoGenerateDelivery';
import { makeStyles } from 'design-system/styles';
import { copyToClipboard } from '@scale/frontend-lib/clipboard';

const description = `
### Description:
- This tool allows you to get more info from a generated delivery batch. Just note down the
UUID from the delivery tool response to get a more detailed response on it.

##### Author: Kamesh Vedula, Ahmad Elshenawy
---
`;

// @ts-ignore
const DetailedInfo: React.FC = () => {
  const [deliveryUUID, setDeliveryUUID] = useState<string>();
  const [loading, setLoading] = useState<boolean>(false);
  const [infoMessage, setInfoMessage] = useState<string>();
  const [deliveryIssues, setDeliveryIssues] = useState<DeliveryTaskProblems | null>(null);
  const enabled = deliveryUUID && !loading;

  const classes = styles();

  const handleUpdate = async () => {
    setLoading(true);
    setInfoMessage('');
    setDeliveryIssues(null);

    try {
      setInfoMessage('Fetching Results...');
      const { success, error, message, deliveryIssues } = await apiFetch(
        null,
        `corp-api/roadrunner-utils/outputs/${deliveryUUID}`,
      );

      if (success) {
        setInfoMessage(message);
        setDeliveryIssues(deliveryIssues);
      } else if (!success && error) {
        setInfoMessage('File Not Found for given UUID.');
      }
    } catch (error) {
      defaultApiFetchErrorHandler(error, { handleHttp: true });
    } finally {
      setLoading(false);
    }
  };

  const buttonText = () => {
    if (!deliveryUUID) return 'Specify Run UUID';
    return 'Generate Detailed Run Report';
  };
  const handleKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
    if (e.key !== 'Enter') {
      return;
    }
    e.stopPropagation();
    handleUpdate();
  };

  const renderOutput = (text: string) => {
    return (
      <div className="response-markdown">
        <SanitizedMarkdown text={text || ''} />
        <style jsx global>{`
          .response-markdown tr {
            border: 1px solid rgba(0, 0, 0, 0.3);
          }

          .response-markdown tr:nth-child(even) {
            background-color: #f2f2f2;
          }

          .response-markdown th {
            border: 1px solid rgb(0, 0, 0, 0.3);
            padding: 6px 10px;
            background-color: #f2f2f2;
          }

          .response-markdown th {
            border: 1px solid rgba(255, 255, 255, 0.3);
            padding: 6px 10px;
            background-color: #222;
            color: white;
          }

          .response-markdown td {
            border: 1px solid rgba(0, 0, 0, 0.3);
            padding: 10px;
          }
        `}</style>
      </div>
    );
  };

  const infoTasksSection = (
    taskIds: string[],
    titleText: string,
    infoText: string,
    hintText?: string,
  ) => {
    return (
      <div className={classes.roundBorderBox}>
        <h4>
          <IconButton
            size="small"
            data-tip="Copy IDs to clipboard"
            onClick={() =>
              copyToClipboard(taskIds.toString(), {
                excludeOuterQuotes: true,
              })
            }
          >
            <FontAwesomeIcon icon="copy" color="lightgrey" />
          </IconButton>
          {titleText} [{taskIds.length}]
        </h4>
        {infoText}
        <div className={classes.taskIdSection}>{JSON.stringify(taskIds)}</div>
        <br />
        {hintText && <i>{hintText}</i>}
      </div>
    );
  };

  return (
    <div className="root">
      <section>
        <SanitizedMarkdown text={description} />
      </section>
      <section>
        <section>
          <Label>Delivery UUID:</Label>
          <TextInput
            fullWidth
            value={deliveryUUID}
            onKeyDown={handleKeyDown}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setDeliveryUUID(e.target.value.trim());
            }}
          />
        </section>
      </section>
      <br />
      <section>
        <Button primary disabled={!enabled || loading} onClick={handleUpdate}>
          {buttonText()}
        </Button>
        {infoMessage && (
          <div>
            <hr />
            <div className={classes.doubleColumnWrapper}>
              <div className={classes.widerColumn}>
                <h3>Run Result Summary</h3>
                {renderOutput(infoMessage)}
              </div>
              <div className={classes.thinnerColumn}>
                <h3>Run Result Issues</h3>
                {deliveryIssues && (
                  <div>
                    {infoTasksSection(
                      deliveryIssues.invalidTasks,
                      'Invalid Tasks',
                      `An invalid task has one of the following:\n
                      - Last non-ignored attempt's ReviewLevel is not L10 or L12\n
                      - Status is not in "canceled", "pending" or "completed"\n`,
                      '* enabling the "Relax Restrictions" option when creating will ignore this check',
                    )}
                    {infoTasksSection(
                      deliveryIssues.skippedTasks,
                      'Skipped Tasks',
                      'Tasks are skipped if they have no fbid in the Task metadata, or if there is no response in the task or the tasks most recent attempt',
                    )}
                    {infoTasksSection(
                      deliveryIssues.alreadyDeliveredTasks,
                      'Already Delivered',
                      'Tasks that have already been marked as delivered',
                    )}
                    {infoTasksSection(
                      deliveryIssues.sensitiveContentTasks,
                      'Sensitive Content Tasks',
                      'Tasks that have sensitive content',
                    )}
                    {infoTasksSection(
                      deliveryIssues.errorTasks,
                      'Errors',
                      'Tasks that encountered some other kind of errora nd failed to be included for delivery',
                    )}
                  </div>
                )}
                {!loading && !deliveryIssues && <p>No delivery issues found</p>}
              </div>
            </div>
          </div>
        )}
      </section>
      <style jsx>{`
        .root .two-columns {
          display: flex;
        }
        .root .two-columns section {
          flex-basis: 50%;
        }
        .root .two-columns section.disabled {
          color: rgba(0, 0, 0, 0.3);
        }
      `}</style>
    </div>
  );
};

export default DetailedInfo;

const styles = makeStyles(_theme => ({
  doubleColumn: {
    columnCount: 2,
    gridColumn: '2',
  },
  doubleColumnWrapper: {
    margin: 6,
    display: 'grid',
    gridTemplateColumns: '6fr 8fr',
    gridGap: '6px',
  },
  widerColumn: {
    gridColumn: '1',
  },
  thinnerColumn: {
    gridColumn: '2',
    padding: '8px',
  },
  taskIdSection: {
    overflow: 'scroll',
    'white-space': 'normal',
    // background: 'grey',
    width: '1000px',
  },
  roundBorderBox: {
    border: 'solid #ccc 1px',
    padding: '0px 10px 20px 10px',
    borderRadius: '8px',
    marginTop: '20px',
    // width: '100%',
  },
}));
